import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Form,
  Select,
  Button,
  Alert,
  Typography,
  Card,
  Row,
  Col,
} from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import { Player } from '@lottiefiles/react-lottie-player';
import downloadBase64Pdf from 'util/downloadBase64Pdf';
import print from 'print-js';
import * as api from 'util/api';
import { inferLabelFromSku } from 'util/inferLabelFromSku';
import labelUnitSizeDisplay from 'util/labelUnitSizeDisplay';
import LabelGeneratorFormContent from './LabelGeneratorFormContent/LabelGeneratorFormContent';
import LabelPreview from './LabelPreview/LabelPreview';
import 'components/GeneratorModal/GeneratorModal.less';
import './NewLabelBuilder.less';

const { Text } = Typography;

const INITIAL_FORM_VALUES = {
  keg: {
    dateType: 'kegged',
  },
  case: {
    dateType: 'bestBefore',
  },
  slab: {
    dateType: 'bestBefore',
  },
  pallet: {
    dateType: 'bestBefore',
  },
};

function packagingTitle(packagingType) {
  if (packagingType === 'slab') return 'Slab';
  if (packagingType === 'case') return 'Case';
  if (packagingType === 'keg') return 'Keg';
  return packagingType;
}

function palletOfPlural(packagingType) {
  if (packagingType === 'slab') return 'slabs';
  if (packagingType === 'case') return 'cases';
  return 'kegs';
}

/** Mirrors LabelGeneratorFormContent date type options: `[selectedPalletType, type].includes(…)`. */
function isDateTypeAllowedForLabelContext(effectiveType, dateType, palletOfType) {
  if (!dateType) return false;
  const row = [palletOfType, effectiveType];
  const hasSlab = row.includes('slab');
  const hasCase = row.includes('case');
  const hasKeg = row.includes('keg');
  const allowed = new Set(['packed', 'useBy']);
  if (hasSlab) allowed.add('canned');
  if (hasCase) allowed.add('bottled');
  if (hasKeg) allowed.add('kegged');
  if (!hasKeg) {
    allowed.add('bestBefore');
    allowed.add('expiry');
  }
  return allowed.has(dateType);
}

const SESSION_PRESERVE_FIELD_KEYS = ['gyle', 'quantity', 'dateType', 'date'];

const NewLabelBuilder = ({
  onPrint,
  generatorStatus,
  labelPDF,
  onGeneratorReset,
}) => {
  const [form] = Form.useForm();
  const watchedCustomProductName = Form.useWatch('customProductName', form);
  const watchedGyle = Form.useWatch('gyle', form);
  const watchedDate = Form.useWatch('date', form);
  const watchedDateType = Form.useWatch('dateType', form);
  const watchedUnitSize = Form.useWatch('unitSize', form);
  const watchedCertifiedFresh = Form.useWatch('certifiedFresh', form);
  const watchedQuantity = Form.useWatch('quantity', form);
  const watchedPalletType = Form.useWatch('palletType', form);
  const watchedShowUnitBarcode = Form.useWatch('showUnitBarcode', form);

  const watchedFormValues = useMemo(() => ({
    customProductName: watchedCustomProductName,
    gyle: watchedGyle,
    date: watchedDate,
    dateType: watchedDateType,
    unitSize: watchedUnitSize,
    certifiedFresh: watchedCertifiedFresh,
    quantity: watchedQuantity,
    palletType: watchedPalletType,
    showUnitBarcode: watchedShowUnitBarcode,
  }), [
    watchedCustomProductName,
    watchedGyle,
    watchedDate,
    watchedDateType,
    watchedUnitSize,
    watchedCertifiedFresh,
    watchedQuantity,
    watchedPalletType,
    watchedShowUnitBarcode,
  ]);
  const [products, setProducts] = useState(null);
  const [productId, setProductId] = useState(null);
  const [skus, setSkus] = useState([]);
  const [skuId, setSkuId] = useState(null);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [ambiguousLegacyKey, setAmbiguousLegacyKey] = useState(null);
  const [palletMode, setPalletMode] = useState(false);
  /** Huaxi (China distributor) slab variant — adds packing date / shelf life / registration number. */
  const [huaxiMode, setHuaxiMode] = useState(false);

  const preservedFieldsByProductRef = useRef({});
  const lastFormResetSigRef = useRef(null);
  const lastProductIdForPreserveRef = useRef(null);
  /** product|sku|ambiguous — unchanged when only pallet checkbox toggles */
  const lastIdentitySigRef = useRef(null);

  useEffect(() => {
    api.getProducts().then(setProducts);
  }, []);

  useEffect(() => {
    if (!productId) {
      setSkus([]);
      setSkuId(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingSkus(true);
    api.getProductSkus(productId)
      .then((data) => {
        if (!cancelled) {
          setSkus(Array.isArray(data) ? data : []);
          setSkuId(null);
          setAmbiguousLegacyKey(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSkus(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const product = useMemo(
    () => (products && productId ? products.find((p) => p.id === productId) : null),
    [products, productId],
  );

  /** Same shape as handleSubmit: `resolveLabelBarcode` needs SKU rows (getProducts omits them). */
  const productWithSkus = useMemo(
    () => (product ? { ...product, skus } : null),
    [product, skus],
  );

  const selectedSku = useMemo(() => {
    if (!skuId || !skus.length) return null;
    return skus.find((s) => String(s.key ?? s.id) === String(skuId)) ?? null;
  }, [skuId, skus]);

  const rawInference = useMemo(
    () => (selectedSku ? inferLabelFromSku(selectedSku) : null),
    [selectedSku],
  );

  useEffect(() => {
    if (!rawInference?.ambiguous) {
      setAmbiguousLegacyKey(null);
      return undefined;
    }
    setAmbiguousLegacyKey((prev) => {
      if (prev && rawInference.options.some((o) => o.legacyKey === prev)) {
        return prev;
      }
      return rawInference.options[0]?.legacyKey ?? null;
    });
    return undefined;
  }, [rawInference]);

  const resolvedOption = useMemo(() => {
    if (!rawInference) return null;
    if (rawInference.ambiguous) {
      return rawInference.options.find((o) => o.legacyKey === ambiguousLegacyKey)
        ?? rawInference.options[0]
        ?? null;
    }
    return rawInference;
  }, [rawInference, ambiguousLegacyKey]);

  const effectiveType = palletMode && resolvedOption ? 'pallet' : resolvedOption?.packagingType;

  const prevSelectionSig = useRef(null);
  useEffect(() => {
    const sig = `${productId}|${skuId}|${ambiguousLegacyKey}|${palletMode}|${huaxiMode}`;
    if (prevSelectionSig.current === null) {
      prevSelectionSig.current = sig;
      return undefined;
    }
    if (prevSelectionSig.current !== sig) {
      prevSelectionSig.current = sig;
      onGeneratorReset();
    }
    return undefined;
  }, [productId, skuId, ambiguousLegacyKey, palletMode, huaxiMode, onGeneratorReset]);

  useEffect(() => {
    if (!product || !resolvedOption || !effectiveType) return;

    const sig = `${productId}|${skuId}|${ambiguousLegacyKey}|${palletMode}`;
    const identitySig = `${productId}|${skuId}|${ambiguousLegacyKey}`;

    if (
      lastIdentitySigRef.current === identitySig
      && lastFormResetSigRef.current !== null
    ) {
      const patch = {
        certifiedFresh: false,
        customProductName: product.name,
        unitSize: resolvedOption.unitSize,
      };
      if (effectiveType === 'pallet') {
        patch.palletType = resolvedOption.packagingType;
      } else {
        patch.palletType = undefined;
      }
      form.setFieldsValue(patch);
      const currentDateType = form.getFieldValue('dateType');
      const palletOf = effectiveType === 'pallet' ? resolvedOption.packagingType : undefined;
      if (huaxiMode && effectiveType === 'slab') {
        form.setFieldsValue({ dateType: 'packed' });
      } else if (
        !currentDateType
        || !isDateTypeAllowedForLabelContext(effectiveType, currentDateType, palletOf)
      ) {
        form.setFieldsValue({ dateType: INITIAL_FORM_VALUES[effectiveType].dateType });
      }
      lastFormResetSigRef.current = sig;
      lastProductIdForPreserveRef.current = productId;
      return;
    }

    const prevPid = lastProductIdForPreserveRef.current;
    if (prevPid !== null && prevPid !== productId) {
      delete preservedFieldsByProductRef.current[prevPid];
    }

    if (
      lastFormResetSigRef.current !== null
      && lastProductIdForPreserveRef.current === productId
      && lastFormResetSigRef.current !== sig
    ) {
      preservedFieldsByProductRef.current[productId] = form.getFieldsValue(
        SESSION_PRESERVE_FIELD_KEYS,
      );
    }

    form.resetFields();
    const base = {
      certifiedFresh: false,
      customProductName: product.name,
      unitSize: resolvedOption.unitSize,
      ...INITIAL_FORM_VALUES[effectiveType],
    };
    if (effectiveType === 'pallet') {
      base.palletType = resolvedOption.packagingType;
    }
    if (huaxiMode && effectiveType === 'slab') {
      base.dateType = 'packed';
    }

    const preserved = preservedFieldsByProductRef.current[productId];
    if (preserved) {
      if (preserved.gyle !== undefined) base.gyle = preserved.gyle;
      if (preserved.quantity !== undefined) base.quantity = preserved.quantity;
      if (preserved.date !== undefined) base.date = preserved.date;
      if (!(huaxiMode && effectiveType === 'slab')) {
        const palletOf = effectiveType === 'pallet' ? resolvedOption.packagingType : undefined;
        if (
          preserved.dateType !== undefined
          && isDateTypeAllowedForLabelContext(effectiveType, preserved.dateType, palletOf)
        ) {
          base.dateType = preserved.dateType;
        }
      }
    }

    form.setFieldsValue(base);

    lastIdentitySigRef.current = identitySig;
    lastFormResetSigRef.current = sig;
    lastProductIdForPreserveRef.current = productId;
  }, [
    product,
    resolvedOption,
    effectiveType,
    form,
    productId,
    skuId,
    ambiguousLegacyKey,
    palletMode,
    huaxiMode,
  ]);

  useEffect(() => {
    if (!resolvedOption || effectiveType !== 'slab' || !huaxiMode) return undefined;
    form.setFieldsValue({ dateType: 'packed' });
    return undefined;
  }, [huaxiMode, effectiveType, resolvedOption, form]);

  const handleSubmit = (formData) => {
    if (!product || !effectiveType) return;
    onPrint({
      type: effectiveType,
      data: {
        /** `skus` lets patchProductLabelBarcode resolve EANs from SKU rows (same as modal flow). */
        product: { ...product, skus },
        formData: {
          ...formData,
          huaxi: effectiveType === 'slab' && huaxiMode,
        },
      },
    });
  };

  const productOptions = useMemo(() => (
    (products ?? []).map((p) => ({ label: p.name, value: p.id }))
  ), [products]);

  const skuOptions = useMemo(() => (
    skus.map((s) => ({
      label: s.name || `SKU ${s.key ?? s.id}`,
      /** API uses `key` as the row id (see PRODUCT_SKU_ATTRIBUTES); `id` is not returned. */
      value: s.key ?? s.id,
    }))
  ), [skus]);

  const showForm = product && selectedSku && resolvedOption && effectiveType
    && !generatorStatus.active
    && !generatorStatus.success;

  const detectedLabelSummary = useMemo(() => {
    if (!resolvedOption) return '';
    return `${palletMode ? 'Pallet — ' : ''}${packagingTitle(resolvedOption.packagingType)} — ${labelUnitSizeDisplay(resolvedOption.unitSize, resolvedOption.packagingType)}`;
  }, [resolvedOption, palletMode]);

  const productsLoading = products === null;
  const selectCommon = {
    size: 'large',
    showSearch: true,
    allowClear: true,
    optionFilterProp: 'label',
    style: { width: '100%' },
  };

  return (
    <div className="new-label-builder">
      <div className="new-label-builder__inner">
        <Card
          className="new-label-builder__card"
          title="Choose product & SKU"
          loading={productsLoading}
        >
          <Form layout="vertical" requiredMark={false}>
            <Row gutter={[16, 0]}>
              <Col xs={24} lg={12}>
                <Form.Item
                  label="Product"
                  htmlFor="new-label-builder-product"
                >
                  <Select
                    {...selectCommon}
                    id="new-label-builder-product"
                    placeholder="Search and select a product"
                    options={productOptions}
                    filterOption={(input, option) => (
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    )}
                    value={productId}
                    onChange={(v) => {
                      setProductId(v ?? null);
                      setSkuId(null);
                      setPalletMode(false);
                      setHuaxiMode(false);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  label="SKU"
                  htmlFor="new-label-builder-sku"
                >
                  <Select
                    {...selectCommon}
                    id="new-label-builder-sku"
                    loading={loadingSkus}
                    disabled={!productId || loadingSkus}
                    placeholder={productId ? 'Select a SKU' : 'Select a product first'}
                    options={skuOptions}
                    filterOption={(input, option) => (
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    )}
                    value={skuId}
                    onChange={(v) => {
                      setSkuId(v ?? null);
                      setPalletMode(false);
                      setHuaxiMode(false);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {selectedSku && rawInference === null && (
          <Alert
            type="warning"
            showIcon
            message="No matching packaging label type"
            description={(
              <>
                This SKU does not match any known slab, case, or keg packaging mapping.
                Use the classic label tabs for edge cases:
                {' '}
                <Link to="/dashboard/label-builder/slab">Slab</Link>
                ,
                {' '}
                <Link to="/dashboard/label-builder/case">Case</Link>
                ,
                {' '}
                <Link to="/dashboard/label-builder/keg">Keg</Link>
                , or
                {' '}
                <Link to="/dashboard/label-builder/pallet">Pallet</Link>
                .
              </>
            )}
          />
        )}

        {rawInference?.ambiguous && (
          <Card
            className="new-label-builder__card"
            title="Packaging match"
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Multiple label types match this SKU — choose one.
            </Text>
            <Form layout="vertical" requiredMark={false}>
              <Form.Item label="Label format" style={{ marginBottom: 0 }}>
                <Select
                  {...selectCommon}
                  allowClear={false}
                  value={ambiguousLegacyKey}
                  onChange={setAmbiguousLegacyKey}
                  options={rawInference.options.map((o) => ({
                    value: o.legacyKey,
                    label: `${packagingTitle(o.packagingType)} — ${labelUnitSizeDisplay(o.unitSize, o.packagingType)}`,
                  }))}
                />
              </Form.Item>
            </Form>
          </Card>
        )}

        {product && !product.tapBadgeUrl && resolvedOption && (
          <Alert
            type="warning"
            showIcon
            message="No tap badge image"
            description="CSR printing uses the tap badge on labels. Add a tap badge for this product, or use the classic label builder while artwork is pending."
          />
        )}

        {generatorStatus.active && (
          <Card className="new-label-builder__card">
            <div className="lottie-message-container">
              <Player
                autoplay
                loop
                src="https://assets2.lottiefiles.com/packages/lf20_qarwrwmv.json"
                style={{ height: '150px', width: '150px' }}
              />
              <span>Generating labels...</span>
            </div>
          </Card>
        )}

        {generatorStatus.error && (
          <Card className="new-label-builder__card">
            <div className="lottie-message-container">
              <Player
                autoplay
                loop={false}
                keepLastFrame
                src="https://assets1.lottiefiles.com/packages/lf20_cjn1jdte.json"
                style={{ height: '150px', width: '150px' }}
              />
            </div>
            <div className="generator-output-container">
              {generatorStatus.error}
            </div>
          </Card>
        )}

        {generatorStatus.success && (
          <Card className="new-label-builder__card">
            <div className="lottie-message-container">
              <Player
                autoplay
                loop={false}
                keepLastFrame
                src="https://assets2.lottiefiles.com/packages/lf20_4qldwfx4.json"
                style={{ height: '150px', width: '150px' }}
              />
            </div>
            {labelPDF && (
              <div className="generator-output-container">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div className="output-buttons-container">
                    <Button
                      type="primary"
                      onClick={() => {
                        downloadBase64Pdf({
                          fileName: `${product?.name ?? 'Labels'} Labels`,
                          base64: labelPDF,
                        });
                      }}
                    >
                      Download PDF
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        print({
                          printable: labelPDF,
                          type: 'pdf',
                          base64: true,
                        });
                      }}
                    >
                      Print PDF
                    </Button>
                  </div>
                  <Button type="default" style={{ marginTop: 16 }} onClick={onGeneratorReset}>
                    Create another label
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {showForm && (
          <Card
            className="new-label-builder__card"
            title="Label details & preview"
          >
            <Form
              id="new-label-builder-form"
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Row gutter={[24, 24]} className="new-label-builder__details-row">
                <Col xs={24} xl={14} className="new-label-builder__form-col">
                  <LabelGeneratorFormContent
                    type={effectiveType}
                    products={productWithSkus ? [productWithSkus] : [product]}
                    form={form}
                    modalOpen
                    lockedUnitSize={resolvedOption.unitSize}
                    lockedPalletType={
                      palletMode ? resolvedOption.packagingType : undefined
                    }
                    unitSizeDisplayLabel={labelUnitSizeDisplay(
                      resolvedOption.unitSize,
                      resolvedOption.packagingType,
                    )}
                    largeTapBadge
                    mergeDetectedLabelFormat
                    detectedLabelSummary={detectedLabelSummary}
                    showPalletPrintOption={!!resolvedOption}
                    palletMode={palletMode}
                    onPalletModeChange={(checked) => {
                      setPalletMode(checked);
                      if (checked) setHuaxiMode(false);
                    }}
                    palletOfPlural={palletOfPlural(resolvedOption.packagingType)}
                    showHuaxiOption={resolvedOption.packagingType === 'slab' && !palletMode}
                    huaxiMode={huaxiMode}
                    onHuaxiModeChange={(checked) => {
                      setHuaxiMode(checked);
                      if (checked) {
                        form.setFieldsValue({ dateType: 'packed' });
                      }
                    }}
                    hideCertifiedFresh
                    compactLayout
                  />
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<ToolOutlined />}
                      loading={generatorStatus.active}
                      block
                      size="large"
                    >
                      {product?.name === 'Chance, Luck & Magic Framboise'
                        ? 'Generate labels and thank Mitch later xo'
                        : 'Generate labels'}
                    </Button>
                  </Form.Item>
                </Col>
                <Col xs={24} xl={10} className="new-label-builder__preview-col">
                  <LabelPreview
                    labelType={effectiveType}
                    product={productWithSkus || product}
                    formValues={watchedFormValues}
                    huaxiMode={huaxiMode}
                    resolvedOption={resolvedOption}
                  />
                </Col>
              </Row>
            </Form>
          </Card>
        )}
      </div>
    </div>
  );
};

NewLabelBuilder.propTypes = {
  onPrint: PropTypes.func.isRequired,
  generatorStatus: PropTypes.shape({
    active: PropTypes.bool,
    error: PropTypes.string,
    success: PropTypes.bool,
  }).isRequired,
  labelPDF: PropTypes.string,
  onGeneratorReset: PropTypes.func.isRequired,
};

NewLabelBuilder.defaultProps = {
  labelPDF: null,
};

export default NewLabelBuilder;
